import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
} from "@mui/material";
import { Email, Phone, LocationOn, AccessTime } from "@mui/icons-material";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import logger from "../utils/logger";
import { trackEvent } from "../utils/analytics";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    logger.log("Form submitted:", formData);
    trackEvent("contact_form_submit");
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const contactInfo = [
    {
      icon: Email,
      title: "Email Us",
      details: "support@loops.com",
      description: "Send us an email and we'll respond within 24 hours",
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+1 (555) 123-4567",
      description: "Mon-Fri from 8am to 6pm PST",
    },
    {
      icon: LocationOn,
      title: "Visit Us",
      details: "123 Tech Street, San Francisco, CA 94105",
      description: "Our headquarters in the heart of Silicon Valley",
    },
    {
      icon: AccessTime,
      title: "Business Hours",
      details: "Monday - Friday: 8:00 AM - 6:00 PM PST",
      description: "We're here to help during business hours",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Helmet>
        <title>Contact Loops — Get in Touch</title>
        <meta name="description" content="Contact the Loops team. We'd love to hear from you — whether you're a restaurant partner or a hungry deal hunter." />
        <meta property="og:title" content="Contact Loops — Get in Touch" />
        <meta property="og:description" content="Contact the Loops team. We'd love to hear from you — whether you're a restaurant partner or a hungry deal hunter." />
        <meta property="og:url" content="https://loopsfood.com/contact" />
        <link rel="canonical" href="https://loopsfood.com/contact" />
      </Helmet>
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
      >
        Contact Us
      </Typography>

      <Typography
        variant="h6"
        sx={{
          color: "#6b7280",
          mb: 8,
          textAlign: "center",
          maxWidth: "600px",
          mx: "auto",
        }}
      >
        Have questions? We'd love to hear from you. Send us a message and we'll
        respond as soon as possible.
      </Typography>

      <Grid container spacing={6}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
            Get in Touch
          </Typography>

          <Grid container spacing={3}>
            {contactInfo.map((info, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: "100%", p: 2 }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          borderRadius: "50%",
                          p: 1,
                          mr: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <info.icon sx={{ fontSize: 20 }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {info.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {info.details}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {info.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
            Send us a Message
          </Typography>

          <Card sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: "#3b82f6",
                      "&:hover": { backgroundColor: "#2563eb" },
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Grid>
      </Grid>

      {/* FAQ Section */}
      <Box
        sx={{
          mt: 8,
          textAlign: "center",
          py: 6,
          backgroundColor: "#f9fafb",
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          Frequently Asked Questions
        </Typography>
        <Typography variant="body1" sx={{ color: "#6b7280", mb: 3 }}>
          Before reaching out, you might find your answer in our FAQ section.
        </Typography>
        <Button
          variant="outlined"
          sx={{
            borderColor: "#3b82f6",
            color: "#3b82f6",
            "&:hover": {
              borderColor: "#2563eb",
              backgroundColor: "#eff6ff",
            },
          }}
        >
          View FAQ
        </Button>
      </Box>
    </Container>
  );
}

export default Contact;
